import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import { FaArrowLeft, FaEye, FaEyeSlash, FaLock, FaUser } from "react-icons/fa";
import Handler from "../Helpers/Handler";
import { useAuth } from "../Contexts/AuthenticateProvider";
import { getCookie, textCapitalize } from "../Helpers/Utils";
import { peekReason } from "../Helpers/webSession";
import { contentLoadingChange } from "../Reducer/componentSlice";
import LoginLeft from "../Assets/images/login-left-card.svg";
import Logo from "../Assets/images/logo.svg";

const RESEND_COOLDOWN_SECONDS = 30;

const formatCountdown = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const Login = () => {
    const dispatch = useDispatch();
    const { logIn } = useAuth();

    const [userData, setUserData] = useState({});
    const [credentialStep, setCredentialStep] = useState(true);
    const [eye, setEye] = useState(true);
    const [busy, setBusy] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [sentTo, setSentTo] = useState("");
    const timerRef = useRef(null);
    // Why the last sign-in ended (idle, signed in elsewhere, password changed ...), kept until the next sign-in.
    const [endedReason] = useState(() => peekReason());

    const handleChange = (e) => setUserData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    const handleOtpChange = (otp) => {
        if (/^[0-9]*$/.test(otp)) setUserData((prev) => ({ ...prev, otp }));
    };

    const startResendTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        let remaining = RESEND_COOLDOWN_SECONDS;
        setSeconds(remaining);
        timerRef.current = setInterval(() => {
            remaining -= 1;
            setSeconds(remaining);
            if (remaining <= 0) clearInterval(timerRef.current);
        }, 1000);
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    const onOtpSend = async () => {
        if (!userData?.identifier?.trim()) {
            toast.error("Enter your email or phone number");
            return;
        }
        if (!userData?.password) {
            toast.error("Enter your password");
            return;
        }

        setBusy(true);
        dispatch(contentLoadingChange(true));
        const response = await Handler({
            method: "post",
            url: "/staff-auth/otp/request",
            data: { identifier: userData.identifier.trim().toLowerCase(), password: userData.password, platform: "web" },
        });
        if (response?.success) {
            setSentTo(response?.data?.data?.phone || "");
            setCredentialStep(false);
            startResendTimer();
            toast.success("OTP sent successfully");
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to send OTP"));
        }
        dispatch(contentLoadingChange(false));
        setBusy(false);
    };

    const onSubmit = async () => {
        if (!/^[0-9]{6}$/.test(userData?.otp || "")) {
            toast.error("Enter a valid 6-digit OTP");
            return;
        }

        setBusy(true);
        dispatch(contentLoadingChange(true));
        const response = await Handler({
            method: "post",
            url: "/staff-auth/otp/verify",
            data: {
                identifier: userData.identifier.trim().toLowerCase(),
                password: userData.password,
                otp: userData.otp,
                platform: "web",
                fcmToken: getCookie("fcmToken") || undefined,
            },
        });
        if (response?.success) {
            clearInterval(timerRef.current);
            toast.success("Login successful");
            logIn(response?.data?.data?.token, response?.data?.data?.staffId, response?.data?.data?.session);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Invalid OTP"));
            setUserData((prev) => ({ ...prev, otp: "" }));
        }
        dispatch(contentLoadingChange(false));
        setBusy(false);
    };

    const handleKeyDown = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        credentialStep ? onOtpSend() : onSubmit();
    };

    const goBack = () => {
        clearInterval(timerRef.current);
        setSeconds(0);
        setUserData((prev) => ({ ...prev, otp: "" }));
        setCredentialStep(true);
    };

    useEffect(() => {
        if (!credentialStep && userData?.otp?.length === 6 && !busy) onSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData.otp]);

    return (
        <div className="login-page d-flex align-items-center justify-content-center">
            <div className="login-card shadow-lg rounded-4 overflow-hidden">
                <div className="row g-0">
                    <div className="col-lg-6 d-none d-lg-flex login-illustration flex-column align-items-center justify-content-center text-center p-5">
                        <img src={LoginLeft} alt="Secure login illustration" className="login-illustration-img mb-4" />
                        <h5 className="fw-semibold mb-2">Manage your fleet with ease</h5>
                        <p className="text-muted mb-0 px-4">Bookings, staff, vehicles and more - all in one place.</p>
                    </div>

                    <div className="col-12 col-lg-6 login-form-pane d-flex flex-column justify-content-center p-4 p-sm-5">
                        <div className="d-flex flex-column align-items-center text-center text-lg-start mb-4">
                            <img src={Logo} alt="logo" className="login-logo-img mb-3" />
                            {credentialStep ? (
                                <>
                                    <h4 className="fw-semibold mb-1">Welcome back</h4>
                                    <p className="text-muted mb-0">Sign in to your staff account to continue</p>
                                </>
                            ) : (
                                <>
                                    <h4 className="fw-semibold mb-1">Verify it's you</h4>
                                    <p className="text-muted mb-0">
                                        {sentTo ? `Enter the 6-digit code sent to ${sentTo}` : "Enter the 6-digit code we just sent you"}
                                    </p>
                                </>
                            )}
                        </div>

                        {endedReason && (
                            <div className="alert alert-warning py-2 mb-3 text-start" role="status">{endedReason.message}</div>
                        )}

                        {credentialStep ? (
                            <>
                                <div className="mb-3 text-start">
                                    <label htmlFor="login-identifier" className="form-label">Email or phone</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white"><FaUser className="text-muted" /></span>
                                        <input
                                            id="login-identifier"
                                            type="text"
                                            name="identifier"
                                            autoComplete="username"
                                            autoFocus
                                            placeholder="you@company.com"
                                            className="form-control"
                                            disabled={busy}
                                            value={userData.identifier || ""}
                                            onChange={handleChange}
                                            onKeyDown={handleKeyDown}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 text-start">
                                    <label htmlFor="login-password" className="form-label">Password</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white"><FaLock className="text-muted" /></span>
                                        <input
                                            id="login-password"
                                            type={eye ? "password" : "text"}
                                            name="password"
                                            autoComplete="current-password"
                                            placeholder="Enter your password"
                                            className="form-control"
                                            disabled={busy}
                                            value={userData.password || ""}
                                            onChange={handleChange}
                                            onKeyDown={handleKeyDown}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            tabIndex={-1}
                                            aria-label={eye ? "Show password" : "Hide password"}
                                            onClick={() => setEye(!eye)}
                                        >
                                            {eye ? <FaEye /> : <FaEyeSlash />}
                                        </button>
                                    </div>
                                </div>

                                <button className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2" disabled={busy} onClick={onOtpSend}>
                                    {busy && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                                    {busy ? "Sending OTP..." : "Continue"}
                                </button>
                            </>
                        ) : (
                            <>
                                <div className="mb-3 text-start">
                                    <label className="form-label">Email or phone</label>
                                    <input className="form-control" disabled value={userData.identifier || ""} />
                                </div>

                                <div className="mb-3 text-start">
                                    <label className="form-label d-block">One-Time Password</label>
                                    <div className="d-flex justify-content-center justify-content-lg-start">
                                        <OtpInput
                                            value={userData.otp || ""}
                                            onChange={handleOtpChange}
                                            numInputs={6}
                                            shouldAutoFocus
                                            renderSeparator={<span className="mx-1"></span>}
                                            renderInput={(props) => (
                                                <input {...props} disabled={busy} className="form-control otp-box text-center" />
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex flex-column flex-sm-row justify-content-sm-between align-items-sm-center gap-2 mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-decoration-none d-inline-flex align-items-center gap-1"
                                        disabled={busy}
                                        onClick={goBack}
                                    >
                                        <FaArrowLeft size={12} /> Change email or password
                                    </button>
                                    {seconds > 0 ? (
                                        <span className="text-muted small">Resend OTP in {formatCountdown(seconds)}</span>
                                    ) : (
                                        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" disabled={busy} onClick={onOtpSend}>
                                            Resend OTP
                                        </button>
                                    )}
                                </div>

                                <button className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2" disabled={busy} onClick={onSubmit}>
                                    {busy && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                                    {busy ? "Verifying..." : "Login"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
